import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApprovalRequest } from '../../types';
import ApprovalDetail from './ApprovalDetail';
import { useApprovalActions } from '../../hooks/useApprovalActions';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const ApprovalDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { fetchApprovals } = useApprovalActions();

  useEffect(() => {
    const loadApproval = async () => {
      if (!id) {
        setError('No approval ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch all approvals and find the one with matching ID
        const approvals = await fetchApprovals();
        const foundApproval = approvals.find(a => a.id === id);
        
        if (!foundApproval) {
          setError('Approval not found');
        } else {
          setApproval(foundApproval);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load approval');
      } finally {
        setLoading(false);
      }
    };

    loadApproval();
  }, [id, fetchApprovals]);

  const handleApprovalAction = async (approvalId: string, action: 'approve' | 'reject', comment?: string) => {
    // After action is completed, redirect back to approvals list
    navigate('/approvals');
  };

  const handleBackToList = () => {
    navigate('/approvals');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading approval details...</p>
        </div>
      </div>
    );
  }

  if (error || !approval) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error || 'Approval Not Found'}
          </h2>
          <p className="text-gray-600 mb-6">
            {error || 'The requested approval could not be found.'}
          </p>
          <button
            onClick={handleBackToList}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Approvals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Breadcrumb */}
      <div className="bg-white border-b px-6 py-3">
        <nav className="flex items-center space-x-2 text-sm">
          <button
            onClick={handleBackToList}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Approvals
          </button>
          <span className="text-gray-500">/</span>
          <span className="text-gray-900 font-medium truncate">
            {approval.title}
          </span>
        </nav>
      </div>

      {/* Approval Detail */}
      <div className="flex-1 overflow-hidden">
        <ApprovalDetail
          approval={approval}
          onApprovalAction={handleApprovalAction}
          className="h-full"
        />
      </div>
    </div>
  );
};

export default ApprovalDetailPage;
